import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { Field, Input, Select, Textarea, Toggle } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { useToast } from '@/components/ui/Toast';
import { FolderPicker } from '@/components/storage/FolderPicker';
import { PolaNama } from './PolaNama';

const TIPE_KOLOM = [
  { value: 'text', label: 'Teks' },
  { value: 'number', label: 'Angka' },
  { value: 'link', label: 'Tautan' },
  { value: 'image', label: 'Gambar' },
  { value: 'html', label: 'HTML' },
  { value: 'date', label: 'Tanggal' },
];

const JADWAL = [
  { value: 'once', label: 'Sekali jalan (tanpa jadwal)' },
  { value: 'hourly', label: 'Setiap jam' },
  { value: 'daily', label: 'Harian' },
  { value: 'weekly', label: 'Mingguan' },
  { value: 'monthly', label: 'Bulanan' },
];

const HARI = ['Minggu', 'Senin', 'Selasa', 'Rabu', 'Kamis', 'Jumat', 'Sabtu'];

/**
 * Menyunting seluruh konfigurasi sebuah job: detail, cakupan crawl, kolom,
 * dan jadwal.
 *
 * Sebelumnya konfigurasi hanya bisa ditentukan saat job dibuat, dan satu
 * selektor yang keliru berarti membuat ulang job dari nol beserta kehilangan
 * riwayat jalannya.
 */
export const EditJobDialog = ({ job, onClose }) => {
  const toast = useToast();
  const queryClient = useQueryClient();

  const [form, setForm] = useState(() => ({
    name: job.name ?? '',
    description: job.description ?? '',
    targetUrl: job.targetUrl ?? '',
    engine: job.engine ?? 'auto',
    outputFormat: job.outputFormat ?? 'table',
    deduplicate: job.deduplicate ?? true,
    itemSelector: job.crawl?.itemSelector ?? '',
    paginationSelector: job.crawl?.paginationSelector ?? '',
    followPattern: job.crawl?.followPattern ?? '',
    depth: job.crawl?.depth ?? 0,
    maxPages: job.crawl?.maxPages ?? 50,
    delayMs: job.crawl?.delayMs ?? 1000,
    fields: (job.fields ?? []).map((f) => ({
      name: f.name ?? '',
      selector: f.selector ?? '',
      type: f.type ?? 'text',
    })),
    schedule: {
      type: job.schedule?.type ?? 'once',
      enabled: job.schedule?.enabled ?? false,
      timeOfDay: job.schedule?.timeOfDay ?? '09:00',
      dayOfWeek: job.schedule?.dayOfWeek ?? 1,
      dayOfMonth: job.schedule?.dayOfMonth ?? 1,
      namePattern: job.schedule?.namePattern ?? '',
      autoSave: job.schedule?.autoSave ?? true,
      outputFormat: job.schedule?.outputFormat ?? 'csv',
      folderId: job.schedule?.folderId ?? '',
    },
  }));

  const set = (kunci, nilai) => setForm((p) => ({ ...p, [kunci]: nilai }));
  const setJadwal = (kunci, nilai) =>
    setForm((p) => ({ ...p, schedule: { ...p.schedule, [kunci]: nilai } }));
  const setKolom = (i, baru) =>
    setForm((p) => ({ ...p, fields: p.fields.map((f, idx) => (idx === i ? baru : f)) }));

  const simpan = useMutation({
    mutationFn: () =>
      api.patch(`/jobs/${job.id}`, {
        name: form.name,
        description: form.description,
        targetUrl: form.targetUrl,
        engine: form.engine,
        outputFormat: form.outputFormat,
        deduplicate: form.deduplicate,
        crawl: {
          depth: Number(form.depth) || 0,
          maxPages: Number(form.maxPages) || 50,
          delayMs: Number(form.delayMs) || 1000,
          itemSelector: form.itemSelector || null,
          paginationSelector: form.paginationSelector || null,
          followPattern: form.followPattern || null,
        },
        fields: form.fields
          .filter((f) => f.name.trim() && f.selector.trim())
          .map((f) => ({ name: f.name, selector: f.selector, type: f.type, attribute: null })),
        schedule: {
          ...form.schedule,
          enabled: form.schedule.enabled && form.schedule.type !== 'once',
          folderId: form.schedule.folderId || null,
        },
      }),
    onSuccess: () => {
      toast.success('Konfigurasi disimpan', {
        description: 'Jalan berikutnya memakai pengaturan baru ini.',
      });
      queryClient.invalidateQueries({ queryKey: ['job', job.id] });
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      onClose();
    },
    onError: (err) =>
      toast.error('Gagal menyimpan', {
        description: err instanceof ApiError ? err.message : 'Periksa isian lalu coba lagi.',
      }),
  });

  const berjadwal = form.schedule.type !== 'once';

  return (
    <Modal
      open
      onClose={onClose}
      title="Sunting Konfigurasi"
      description="Perubahan berlaku untuk jalan berikutnya. Hasil yang sudah ada tidak berubah."
      size="lg"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Batal
          </Button>
          <Button loading={simpan.isPending} onClick={() => simpan.mutate()}>
            Simpan Perubahan
          </Button>
        </div>
      }
    >
      <div className="space-y-5">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Nama Job" required>
            {(p) => <Input {...p} value={form.name} onChange={(e) => set('name', e.target.value)} />}
          </Field>
          <Field label="URL Sumber" required>
            {(p) => (
              <Input
                {...p}
                type="url"
                value={form.targetUrl}
                onChange={(e) => set('targetUrl', e.target.value)}
              />
            )}
          </Field>
        </div>

        <Field label="Deskripsi">
          {(p) => (
            <Textarea
              {...p}
              rows={2}
              value={form.description}
              onChange={(e) => set('description', e.target.value)}
            />
          )}
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Mesin Crawl">
            {(p) => (
              <Select
                {...p}
                value={form.engine}
                onChange={(e) => set('engine', e.target.value)}
                options={[
                  { value: 'auto', label: 'Otomatis' },
                  { value: 'static', label: 'Cepat - HTML statis' },
                  { value: 'dynamic', label: 'Browser Chrome - halaman JavaScript' },
                  { value: 'playwright', label: 'Browser Playwright - kalau Chrome gagal' },
                ]}
              />
            )}
          </Field>
          <Field label="Format Output">
            {(p) => (
              <Select
                {...p}
                value={form.outputFormat}
                onChange={(e) => set('outputFormat', e.target.value)}
                options={[
                  { value: 'table', label: 'Tabel' },
                  { value: 'json', label: 'JSON' },
                  { value: 'csv', label: 'CSV' },
                ]}
              />
            )}
          </Field>
        </div>

        <div>
          <p className="mb-2 text-sm font-bold text-[var(--text-primary)]">Cakupan Crawl</p>
          <div className="grid gap-4 sm:grid-cols-3">
            <Field label="Maks Halaman">
              {(p) => (
                <Input
                  {...p}
                  type="number"
                  min="1"
                  value={form.maxPages}
                  onChange={(e) => set('maxPages', e.target.value)}
                />
              )}
            </Field>
            <Field label="Kedalaman" hint="0 = halaman itu saja">
              {(p) => (
                <Input
                  {...p}
                  type="number"
                  min="0"
                  max="5"
                  value={form.depth}
                  onChange={(e) => set('depth', e.target.value)}
                />
              )}
            </Field>
            <Field label="Jeda (ms)" hint="Antar permintaan">
              {(p) => (
                <Input
                  {...p}
                  type="number"
                  min="0"
                  value={form.delayMs}
                  onChange={(e) => set('delayMs', e.target.value)}
                />
              )}
            </Field>
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Selektor Item" hint="Elemen yang berulang, mis. .product-card">
            {(p) => (
              <Input
                {...p}
                value={form.itemSelector}
                className="font-mono text-sm"
                onChange={(e) => set('itemSelector', e.target.value)}
              />
            )}
          </Field>
          <Field label="Selektor Halaman Berikutnya">
            {(p) => (
              <Input
                {...p}
                value={form.paginationSelector}
                className="font-mono text-sm"
                onChange={(e) => set('paginationSelector', e.target.value)}
              />
            )}
          </Field>
        </div>

        <div>
          <div className="mb-2 flex items-center justify-between">
            <p className="text-sm font-bold text-[var(--text-primary)]">
              Kolom ({form.fields.length})
            </p>
            <Button
              variant="secondary"
              size="sm"
              icon={Plus}
              onClick={() =>
                set('fields', [...form.fields, { name: '', selector: '', type: 'text' }])
              }
            >
              Tambah Kolom
            </Button>
          </div>

          <div className="space-y-2">
            {form.fields.map((f, i) => (
              <div key={i} className="flex flex-wrap items-center gap-2">
                <Input
                  value={f.name}
                  aria-label={`Nama kolom ${i + 1}`}
                  placeholder="Nama kolom"
                  className="h-10 w-36 px-3 text-sm font-semibold"
                  onChange={(e) => setKolom(i, { ...f, name: e.target.value })}
                />
                <div className="w-28">
                  <Select
                    value={f.type}
                    aria-label={`Tipe kolom ${i + 1}`}
                    options={TIPE_KOLOM}
                    onChange={(e) => setKolom(i, { ...f, type: e.target.value })}
                  />
                </div>
                <Input
                  value={f.selector}
                  aria-label={`Selektor kolom ${i + 1}`}
                  placeholder="selektor CSS"
                  className="h-10 min-w-40 flex-1 px-3 font-mono text-xs"
                  onChange={(e) => setKolom(i, { ...f, selector: e.target.value })}
                />
                <Button
                  variant="ghost"
                  size="sm"
                  icon={Trash2}
                  iconOnly
                  aria-label={`Hapus kolom ${i + 1}`}
                  onClick={() =>
                    set('fields', form.fields.filter((_, idx) => idx !== i))
                  }
                />
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-4 rounded-lg border border-[var(--border-subtle)] p-4">
          <p className="text-sm font-bold text-[var(--text-primary)]">Penjadwalan</p>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Frekuensi">
              {(p) => (
                <Select
                  {...p}
                  value={form.schedule.type}
                  options={JADWAL}
                  onChange={(e) => setJadwal('type', e.target.value)}
                />
              )}
            </Field>
            {berjadwal && (
              <Field label="Jam" hint="Waktu Asia/Jakarta">
                {(p) => (
                  <Input
                    {...p}
                    type="time"
                    value={form.schedule.timeOfDay}
                    onChange={(e) => setJadwal('timeOfDay', e.target.value)}
                  />
                )}
              </Field>
            )}
          </div>

          {form.schedule.type === 'weekly' && (
            <Field label="Hari">
              {(p) => (
                <Select
                  {...p}
                  value={String(form.schedule.dayOfWeek)}
                  options={HARI.map((h, i) => ({ value: String(i), label: h }))}
                  onChange={(e) => setJadwal('dayOfWeek', Number(e.target.value))}
                />
              )}
            </Field>
          )}

          {form.schedule.type === 'monthly' && (
            <Field label="Tanggal">
              {(p) => (
                <Input
                  {...p}
                  type="number"
                  min="1"
                  max="31"
                  value={form.schedule.dayOfMonth}
                  onChange={(e) => setJadwal('dayOfMonth', Number(e.target.value))}
                />
              )}
            </Field>
          )}

          {berjadwal && (
            <>
              <Toggle
                checked={form.schedule.enabled}
                onChange={(v) => setJadwal('enabled', v)}
                label="Aktifkan jadwal"
                description="Job berjalan sendiri sesuai frekuensi di atas."
              />

              <Toggle
                checked={form.schedule.autoSave}
                onChange={(v) => setJadwal('autoSave', v)}
                label="Simpan hasil otomatis"
                description="Tiap jalan menyimpan hasilnya ke Penyimpanan Pribadi."
              />

              {form.schedule.autoSave && (
                <>
                  <PolaNama
                    nilai={form.schedule.namePattern}
                    tipe={form.schedule.type}
                    namaJob={form.name}
                    onUbah={(v) => setJadwal('namePattern', v)}
                  />

                  <div className="grid gap-4 sm:grid-cols-2">
                    <Field label="Format berkas">
                      {(p) => (
                        <Select
                          {...p}
                          value={form.schedule.outputFormat}
                          options={[
                            { value: 'csv', label: 'CSV' },
                            { value: 'json', label: 'JSON' },
                            { value: 'ndjson', label: 'NDJSON' },
                          ]}
                          onChange={(e) => setJadwal('outputFormat', e.target.value)}
                        />
                      )}
                    </Field>
                    <Field label="Folder tujuan">
                      {(p) => (
                        <FolderPicker
                          {...p}
                          value={form.schedule.folderId}
                          onChange={(v) => setJadwal('folderId', v)}
                        />
                      )}
                    </Field>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </Modal>
  );
};
